import { Disclosure } from '@headlessui/react'
import ChevronDownIcon from '@heroicons/react/solid/esm/ChevronDownIcon.js'
import PlusIcon from '@heroicons/react/solid/esm/PlusIcon.js'
import { clsx } from 'clsx'
import { useLocation } from 'react-router'
import { Link, NavLink } from 'react-router-dom'

import { Collection } from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { SpaceImage } from '@tribeplatform/react-components/Space'
import { SystemSpaceSlug, useSpaceUtil } from '@tribeplatform/react-sdk/hooks'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'

import { useSlateKitContext } from '../../hooks/index.js'

export const CollectionMenuCollapsible = ({
  collections,
  createSpace,
  canCreateSpace,
  ...rest
}: {
  collections: Collection[]
  createSpace: (space: { collectionId: string }) => void
  canCreateSpace: boolean
}) => {
  const { generatePath, matchPath } = useSpaceUtil()
  const location = useLocation()
  const {
    kit: { userStorage: storage },
  } = useSlateKitContext()

  const spaceCollections =
    storage.getItem<{ id: string; isExpanded: boolean }[]>('spaceCollections')

  return (
    <NavVertical {...rest}>
      <div className="space-y-2">
        {collections.map(collection => {
          const hasSpaces = collection?.spaces?.totalCount
          const isActive = matchPath(
            SystemSpaceSlug.COLLECTION,
            location.pathname,
            { id: collection.id },
          )

          if (!hasSpaces && !canCreateSpace) {
            return null
          }
          return (
            <Disclosure
              key={collection.id}
              defaultOpen={
                spaceCollections?.[collection?.id]?.isExpanded ?? true
              }
            >
              {({ open }) => (
                <NavVertical.Group>
                  <NavVertical.Item
                    // className="group"
                    active={!!isActive}
                    as={Link}
                    to={generatePath(SystemSpaceSlug.COLLECTION, {
                      id: collection.id,
                    })}
                  >
                    <div
                      onClick={e => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                      className={clsx(
                        'items-center justify-center w-6 h-6 rounded-md me-3 -ms-1',
                        isActive
                          ? 'hover:bg-background-hovered'
                          : 'hover:bg-background-pressed',
                      )}
                    >
                      <Disclosure.Button as="div" role="navigation">
                        <ChevronDownIcon
                          onClick={() => {
                            storage.setItem('spaceCollections', {
                              ...spaceCollections,
                              [collection?.id]: {
                                id: collection?.id,
                                isExpanded: !open,
                                name: collection?.name,
                              },
                            })
                          }}
                          className={clsx(
                            'w-6 h-6 transform transition-transform',
                            !open ? '-rotate-90 rtl:rotate-90' : '',
                          )}
                        />
                      </Disclosure.Button>
                    </div>
                    <span className="truncate flex-grow font-semibold">
                      {collection.name}
                    </span>
                  </NavVertical.Item>
                  <Disclosure.Panel>
                    <NavVertical.Group>
                      {collection?.spaces?.edges?.map(({ node: space }) => {
                        const isSpaceActive = matchPath(
                          space,
                          location.pathname,
                        )
                        return (
                          <NavVertical.Item
                            as={NavLink}
                            key={space.id}
                            to={space?.relativeUrl || '/'}
                            active={!!isSpaceActive}
                          >
                            <div className="me-3 -ms-1">
                              <SpaceImage space={space} size="xs" />
                            </div>
                            <div className="truncate flex-grow">
                              {space.name}
                            </div>
                            {!!space?.authMemberProps?.unreadPostsCount && (
                              <span className="text-xs rounded-md bg-background-pressed font-medium py-0.5 px-2 text-content-on-background-subdued">
                                {space?.authMemberProps?.unreadPostsCount}
                              </span>
                            )}
                          </NavVertical.Item>
                        )
                      })}
                      {!hasSpaces ? (
                        <NavVertical.Item
                          as="button"
                          onClick={() => {
                            createSpace({
                              collectionId: collection.id,
                            })
                          }}
                        >
                          <span className="w-6 h-6 -ms-1 me-3 flex justify-center items-center">
                            <PlusIcon className="w-5 h-5" />
                          </span>
                          <span className="truncate">
                            <T
                              id="Generics.AddSpace"
                              defaultMessage="Add {SPACE}"
                            />
                          </span>
                        </NavVertical.Item>
                      ) : null}
                    </NavVertical.Group>
                  </Disclosure.Panel>
                </NavVertical.Group>
              )}
            </Disclosure>
          )
        })}
      </div>
    </NavVertical>
  )
}
